import React, { useState, useEffect, useCallback } from 'react';
import {
  Table, Button, Space, message, Modal, Select,
} from 'antd';
import { parseISO, format } from 'date-fns';
import Rest from 'tools/rest';
import IntegerQtyInput from 'actions/materials/components/integerQtyInput';
import {
  formatQuantityWithUnit,
  materialDisplayUnit,
  parseMaterialIntegerQty,
  availableQtyFloor,
  isQuantityExplicitZero,
} from 'actions/materials/utils';

const RequestMaterialsTab = ({
  requestId,
  currentUser,
  onAfterMovesChange,
}) => {
  const [moves, setMoves] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inventory, setInventory] = useState([]);
  const [invLoading, setInvLoading] = useState(false);
  const [consumeOpen, setConsumeOpen] = useState(false);
  const [inventoryId, setInventoryId] = useState(null);
  const [consumeQty, setConsumeQty] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const loadMoves = useCallback(() => {
    if (!requestId) return;
    setLoading(true);
    Rest.get('/api/v1/material_moves.json', {
      params: {
        per: 200,
        page: 1,
        search: { request_id: requestId },
      },
    })
      .then((r) => setMoves(r.data.material_moves || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [requestId]);

  const loadInventory = useCallback(() => {
    if (!currentUser?.id) return;
    setInvLoading(true);
    Rest.get('/api/v1/inventories/my.json')
      .then((r) => setInventory(r.data.inventories || []))
      .catch(() => {})
      .finally(() => setInvLoading(false));
  }, [currentUser?.id]);

  useEffect(() => {
    loadMoves();
  }, [loadMoves]);

  useEffect(() => {
    if (consumeOpen) loadInventory();
  }, [consumeOpen, loadInventory]);

  const handleOpenConsume = () => {
    setInventoryId(null);
    setConsumeQty(null);
    setConsumeOpen(true);
  };

  const handleConsumeSubmit = () => {
    if (!inventoryId) {
      message.error('Выберите материал из инвентаря');
      return;
    }
    if (isQuantityExplicitZero(consumeQty)) {
      message.error('Количество должно быть больше 0');
      return;
    }
    const q = parseMaterialIntegerQty(consumeQty);
    if (q === null) {
      message.error('Укажите целое количество не меньше 1');
      return;
    }
    const row = inventory.find((i) => i.id === inventoryId);
    if (!row) return;
    const available = availableQtyFloor(row);
    if (q > available) {
      message.error(
        `Недостаточно (доступно: ${formatQuantityWithUnit(available, materialDisplayUnit(row))})`
      );
      return;
    }

    setSubmitting(true);
    Rest.post('/api/v1/material_moves.json', {
      material_move: {
        source_type: 'Inventory',
        source_user_id: currentUser.id,
        material_id: row.material_id,
        destination_type: 'Request',
        destination_id: requestId,
        quantity: q,
      },
    })
      .then(() => {
        message.success('Материал списан на задачу');
        setConsumeOpen(false);
        loadMoves();
        onAfterMovesChange?.();
      })
      .catch((err) => {
        message.error(err.response?.data?.errors?.join?.(', ') || 'Ошибка списания');
      })
      .finally(() => setSubmitting(false));
  };

  const columns = [
    {
      title: 'Материал',
      dataIndex: 'material_name',
      width: '18%',
    },
    {
      title: 'Кол-во',
      dataIndex: 'quantity',
      width: '10%',
      render: (qty, record) => formatQuantityWithUnit(qty, materialDisplayUnit(record)),
    },
    {
      title: 'Кто списал',
      dataIndex: 'written_off_by_name',
      width: '22%',
      render: (v) => v || '—',
    },
    {
      title: 'Дата',
      dataIndex: 'created_at',
      width: '16%',
      render: (d) => (d ? format(parseISO(d), 'dd.MM.yyyy HH:mm') : '—'),
    },
  ];

  return (
    <React.Fragment>
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        {currentUser?.id && (
          <Button
            type="primary"
            onClick={handleOpenConsume}
          >
            Списать материал на задачу
          </Button>
        )}
        <Table
          rowKey="id"
          loading={loading}
          size="small"
          columns={columns}
          dataSource={moves}
          pagination={false}
          locale={{ emptyText: 'Списаний по задаче пока нет' }}
        />
      </Space>

      <Modal
        title="Списание на задачу"
        open={consumeOpen}
        onCancel={() => setConsumeOpen(false)}
        onOk={handleConsumeSubmit}
        confirmLoading={submitting}
        okText="Списать"
        cancelText="Отмена"
        destroyOnHidden
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <div style={{ marginBottom: 8 }}>Материал из инвентаря</div>
            <Select
              showSearch
              optionFilterProp="label"
              value={inventoryId}
              placeholder="Выберите позицию"
              loading={invLoading}
              style={{ width: '100%' }}
              onChange={setInventoryId}
              options={inventory.map((i) => ({
                value: i.id,
                label: `${i.material_name}${i.category_name ? ` (${i.category_name})` : ''} — ост. ${formatQuantityWithUnit(i.quantity, materialDisplayUnit(i))}`,
              }))}
            />
          </div>
          <div>
            <div style={{ marginBottom: 8 }}>Количество</div>
            <IntegerQtyInput
              value={consumeQty}
              onChange={setConsumeQty}
              style={{ width: 160 }}
            />
          </div>
        </Space>
      </Modal>
    </React.Fragment>
  );
};

export default RequestMaterialsTab;
